/**
 * One backend document row, turned into the seven things a table cell renders.
 *
 * Pure on purpose: everything the screen shows about a file — its extension, its icon, its
 * size, its date, who uploaded it, what is happening to it — is a decision, and decisions
 * that live in JSX are decisions nobody can test. The component below this file does no
 * formatting at all; it places strings.
 *
 * The uploader name is joined here rather than on the server (see the module docstring on
 * `packages/convex/convex/projectDocuments.ts`): the org user map is a subscription the
 * shell already holds, so resolving it in the browser costs nothing and a per-row
 * `ctx.db.get` would.
 */

import type { api } from "@repo/convex/api";
import { type EmbeddedProvenance, UNRESOLVED_USER_LABEL } from "@repo/shared";
import type { FunctionReturnType } from "convex/server";
import type { LucideIcon } from "lucide-react";
import {
  type DocumentPreviewKind,
  type FileKind,
  getFileExtensionLabel,
  getFileKind,
  getFileTypeIcon,
  getPreviewKind,
} from "@/lib/file-types";
import { formatFileSize, formatSmartDate } from "@/lib/formatters";
import { isTranscriptOffered } from "./document-processing-view";
import {
  type DocumentProvenanceLine,
  formatDocumentProvenance,
  primaryEmbeddedProvenance,
  type SourceArchive,
} from "./document-provenance";
import {
  GOOGLE_IMPORT_COPY,
  isGoogleOwnedDocument,
  resolveGoogleSourceLabel,
} from "./google-import-view";
import { isActiveDocumentStatus } from "./processing-progress";

export type ProjectDocumentPage = FunctionReturnType<typeof api.projectDocuments.list>;
export type ProjectDocumentRow = ProjectDocumentPage["items"][number];
export type DocumentSource = ProjectDocumentRow["origin"];
export type DocumentSourceKind = DocumentSource["kind"];
export type DocumentStatus = ProjectDocumentRow["status"];
/** When this document's external source was last asked about, and what the answer was. */
export type DocumentExternalSourceCheck = NonNullable<ProjectDocumentRow["externalSourceCheck"]>;
/** The run carrying older versions' citations onto the version on screen. */
export type DocumentCitationCarryover = NonNullable<ProjectDocumentRow["citationCarryover"]>;
export type DocumentStatusKind = DocumentStatus["kind"];

/** The design's status vocabulary, in the order a file moves through it. */
export const DOCUMENT_STATUS_LABELS: Record<DocumentStatusKind, string> = {
  queued: "Queued",
  parsing: "Parsing",
  waiting_for_input: "Waiting for you",
  summarizing: "Summarizing",
  ready: "Ready",
  failed: "Failed",
  cancelled: "Stopped",
  not_processed: "Not processed",
};

/**
 * Where each status sits in the pipeline, and therefore the order the Status filter lists
 * them in.
 *
 * A `Record` keyed by the status kind rather than an array of them: a new kind on the
 * backend has to be given a position here before this file compiles, whereas a hand-written
 * array would simply omit it and drop the status out of the filter with nothing going red.
 */
const DOCUMENT_STATUS_RANK = {
  queued: 0,
  parsing: 1,
  // Between the two parse beats: the file has been probed and nothing further will happen
  // until its person answers, which is where the pipeline actually stops.
  waiting_for_input: 2,
  summarizing: 3,
  ready: 4,
  failed: 5,
  // Past the outcomes, because it is not one: the run ended because a person ended it. Ahead
  // of "Not processed" — a stopped file was on its way and was switched off, which is one
  // step further along than one nothing has ever queued.
  cancelled: 6,
  not_processed: 7,
} as const satisfies Record<DocumentStatusKind, number>;

/** The status kinds a Status filter offers, newest-first through the pipeline. */
export const DOCUMENT_STATUS_ORDER: DocumentStatusKind[] = (
  Object.keys(DOCUMENT_STATUS_RANK) as DocumentStatusKind[]
).sort((left, right) => DOCUMENT_STATUS_RANK[left] - DOCUMENT_STATUS_RANK[right]);

/**
 * What the TYPE badge draws. A document Google owns names its editor (`G Sheet`) rather than the
 * extension it was exported as (`XLSX`), because the type a person recognises is the one they
 * picked — and `uppercase` travels with the label because `G Sheet` is a name while `PDF` is an
 * extension.
 */
export type DocumentTypeBadge = {
  label: string;
  uppercase: boolean;
  /** The file came from a provider rather than from an upload — the badge carries a glyph. */
  isExternal: boolean;
};

export type DocumentRowView = {
  artifactId: string;
  name: string;
  /**
   * The name the bytes arrived under. It is what the TYPE badge and the icon are read from,
   * and it is the extension a rename is held to — the backend compares against this one, not
   * against whatever the display name currently ends in.
   */
  fileName: string;
  /** Uppercase extension for the TYPE column — never a MIME string. `null` = show a dash. */
  extensionLabel: string | null;
  /** The TYPE badge itself: the Google editor for a document Google owns, the extension otherwise. */
  typeBadge: DocumentTypeBadge | null;
  /**
   * What opening this row does — the one decision the NAME cell and the ⋯ menu both read, so a
   * click on the name and the item offering the same thing can never disagree.
   */
  openAction: DocumentOpenAction;
  /**
   * Whether a rename of this row keeps the file's extension. False for a document Google owns:
   * it is named without one there, so the editor shows no extension chip and sends none back.
   */
  holdsFileExtension: boolean;
  kind: FileKind;
  icon: LucideIcon;
  /** Formatted size, or `null` when the artifact carries no byte count (a link has none). */
  sizeLabel: string | null;
  /** The same fact `sizeLabel` formats, kept raw so a size sort compares bytes, not text. */
  sizeBytes: number | null;
  uploadedAt: number;
  uploadedLabel: string;
  uploadedByName: string;
  uploadedByViewer: boolean;
  statusKind: DocumentStatusKind;
  statusLabel: string;
  /**
   * Extra wording for a status that would otherwise read as a defect, shown as hover text
   * on the status cell. It carries only what no popover does — a Ready file this deployment
   * would parse differently today, a file nothing has read, a stop somebody made. Anything a
   * person has to *act* on belongs on the cell or in the panel: hover text discloses nothing
   * to a touch screen and nothing to a reader who has no reason to hover.
   */
  statusHint: string | null;
  /**
   * Only `failed` is coloured. Keeping the flag here rather than re-testing the kind in
   * JSX keeps that rule in one place.
   */
  isFailed: boolean;
  /**
   * The pipeline is working on this file right now, so the status text earns a spinner.
   * Shares `isActiveDocumentStatus` with the progress weights, so "the row looks busy" and
   * "the batch bar counts it as in progress" cannot disagree.
   */
  isActive: boolean;
  downloadUrl: string | null;
  /** Where the file came from — one immutable origin, never the list of places it is used. */
  origin: DocumentSource;
  /**
   * The archive this file was unpacked from, and its path inside it (LAT-1022). `null` for
   * every ordinary upload, which is almost every row.
   *
   * Kept beside the formatted label because the free-text filter searches the archive name and
   * the whole entry path — including the filename, which the label deliberately omits.
   */
  sourceArchive: SourceArchive | null;
  /**
   * The documents this file was carried inside, and where in each of them. Empty for every row
   * that is not an embedded child, which is almost every row.
   *
   * Kept beside the formatted line for the same reason `sourceArchive` is: the line names one
   * parent and the free-text filter searches every one of them, anchors included.
   */
  embeddedIn: readonly EmbeddedProvenance[];
  /**
   * The muted second line under NAME — `report.zip › germany/2024` for a file unpacked from a
   * zip, `HLD_Details.xlsx › Sheet2!P32` for one a workbook carried. `null` for an ordinary
   * upload, which is almost every row.
   */
  provenance: DocumentProvenanceLine | null;
};

export type DocumentRowViewContext = {
  /** `null` while the org user list is still loading — names degrade, rows still render. */
  userMap: Map<string, { fullName: string }> | null;
  currentUserId: string | null;
};

/**
 * The name to type the file from — the uploaded filename, not the label on the row.
 *
 * A rename replaces the name a person reads while the bytes, and the filename they arrived
 * under, stay exactly as they were. Deriving the TYPE badge and the icon from the label would
 * make a renamed row draw whatever its new name happens to end in. `fileName` is optional
 * because a row from a backend that has not started sending it yet is still a valid row, and
 * for every file nobody has renamed the two names are the same string anyway.
 */
function resolveTypingName(row: ProjectDocumentRow): string {
  return "fileName" in row && typeof row.fileName === "string" ? row.fileName : row.name;
}

export function buildDocumentRowView(
  row: ProjectDocumentRow,
  context: DocumentRowViewContext
): DocumentRowView {
  const typingName = resolveTypingName(row);
  const kind = getFileKind(typingName, row.mimeType ?? undefined);
  const sourceArchive = row.sourceArchive ?? null;
  const embeddedIn = row.embeddedIn ?? EMPTY_EMBEDDINGS;
  const extensionLabel = getFileExtensionLabel(typingName);
  const isProviderOwned = row.externalSource ? isGoogleOwnedDocument(row.externalSource) : false;

  return {
    artifactId: row.artifactId,
    name: row.name,
    fileName: typingName,
    extensionLabel,
    typeBadge: resolveTypeBadge(row, extensionLabel),
    openAction: resolveDocumentOpenAction(documentOpenSourceFromRow(row)),
    holdsFileExtension: !isProviderOwned,
    kind,
    icon: getFileTypeIcon(kind),
    sizeLabel: formatFileSize(row.fileSizeBytes),
    sizeBytes: row.fileSizeBytes,
    uploadedAt: row.uploadedAt,
    uploadedLabel: formatSmartDate(row.uploadedAt),
    // An uploader who has left the organization is no longer in the map. The shared
    // unresolved-user label is the honest answer; inventing the raw id would put a technical
    // ID on a screen whose whole brief is that it carries none.
    uploadedByName: context.userMap?.get(row.uploadedBy)?.fullName ?? UNRESOLVED_USER_LABEL,
    uploadedByViewer: context.currentUserId !== null && row.uploadedBy === context.currentUserId,
    statusKind: row.status.kind,
    statusLabel: DOCUMENT_STATUS_LABELS[row.status.kind],
    statusHint: resolveStatusHint(row.status),
    isFailed: row.status.kind === "failed",
    isActive: isActiveDocumentStatus(row.status.kind),
    downloadUrl: row.downloadUrl,
    origin: row.origin,
    sourceArchive,
    embeddedIn,
    provenance: resolveRowProvenance(sourceArchive, embeddedIn),
  };
}

/** One array for every row that has no embedding, so a render never allocates a fresh empty. */
const EMPTY_EMBEDDINGS: readonly EmbeddedProvenance[] = [];

/**
 * Which container the second line names when a row could answer with either.
 *
 * A zip entry re-embedded in a workbook is not a shape the pipeline produces, and if it ever
 * were, the workbook is the container a reader can still open: the archive is bytes nobody
 * kept.
 */
function resolveRowProvenance(
  sourceArchive: SourceArchive | null,
  embeddedIn: readonly EmbeddedProvenance[]
): DocumentProvenanceLine | null {
  const embedded = primaryEmbeddedProvenance(embeddedIn);
  if (embedded) return formatDocumentProvenance({ kind: "embedded", provenance: embedded });
  return sourceArchive ? formatDocumentProvenance({ kind: "archive", sourceArchive }) : null;
}

/**
 * The badge over the extension for an imported document: what a person picked in Drive, not
 * what the export happened to produce.
 *
 * A file Drive merely *stored* keeps its own extension and only gains the cloud glyph — a PDF
 * picked from Drive is a PDF, and "Google Drive" over it would replace the one fact the column
 * exists to state with the place it happened to be picked from.
 */
function resolveTypeBadge(
  row: ProjectDocumentRow,
  extensionLabel: string | null
): DocumentTypeBadge | null {
  if (row.externalSource) {
    const editorLabel = resolveGoogleSourceLabel(row.externalSource);
    if (editorLabel) return { label: editorLabel, uppercase: false, isExternal: true };
    return extensionLabel
      ? { label: extensionLabel, uppercase: true, isExternal: true }
      : { label: GOOGLE_IMPORT_COPY.driveShortLabel, uppercase: false, isExternal: true };
  }
  return extensionLabel ? { label: extensionLabel, uppercase: true, isExternal: false } : null;
}

/**
 * What opening a document does — one decision, asked by the NAME cell, the ⋯ menu and a
 * citation in the evidence popover alike.
 *
 * `null` is a row with nothing to open yet: no transcript, no provider page and no bytes.
 */
export type DocumentOpenAction =
  | { kind: "transcript" }
  | { kind: "preview"; previewKind: DocumentPreviewKind; url: string }
  /** A document Google owns, opened at Google. */
  | { kind: "source"; href: string }
  | { kind: "download"; href: string }
  | null;

/**
 * The four facts the open decision is made from, and nothing else.
 *
 * Structural rather than a table row: the same decision is asked of a document by the NAME
 * cell, by the ⋯ menu and by a citation in the evidence popover, and a citation carries a
 * name, a type and a signed URL rather than a `projectDocuments.list` row.
 */
export type DocumentOpenSource = {
  /** The name the bytes arrived under — what the preview kind is read from. */
  fileName: string;
  mimeType: string | null;
  downloadUrl: string | null;
  transcriptOffered: boolean;
  externalSource?: ProjectDocumentRow["externalSource"];
};

/** What a Documents row states about itself when it asks the question. */
export function documentOpenSourceFromRow(row: ProjectDocumentRow): DocumentOpenSource {
  return {
    fileName: resolveTypingName(row),
    mimeType: row.mimeType ?? null,
    downloadUrl: row.downloadUrl,
    transcriptOffered: isTranscriptOffered(row),
    externalSource: row.externalSource,
  };
}

/**
 * Precedence: a transcript, then the document at its provider, then a body this app can draw,
 * then the bytes.
 *
 * One decision with three askers — the NAME cell, the ⋯ menu and the evidence popover — so a
 * file opens the same way wherever a person meets it.
 *
 * The transcript comes first because it is the words themselves rather than the container they
 * arrived in. A document Google owns comes next: the file this app holds is an export made so
 * the pipeline could read it, and handing a person a Word copy of their own Google Doc is not
 * opening the document they clicked. Preview is offered from bytes and a type alone — whatever
 * the processing status, and whatever the browser, since every body the app draws it draws
 * itself — so nothing here waits on a parse, or on a plugin, to show a file.
 */
export function resolveDocumentOpenAction(source: DocumentOpenSource): DocumentOpenAction {
  if (source.transcriptOffered) return { kind: "transcript" };

  const externalSource = source.externalSource;
  if (externalSource?.webViewLink && isGoogleOwnedDocument(externalSource)) {
    return { kind: "source", href: externalSource.webViewLink };
  }

  const downloadUrl = source.downloadUrl;
  if (!downloadUrl) return null;

  const previewKind = getPreviewKind(source.fileName, source.mimeType);
  if (previewKind !== null) {
    return { kind: "preview", previewKind, url: downloadUrl };
  }
  return { kind: "download", href: downloadUrl };
}

/**
 * Why a status says what it says, for the cases where the single word is not the whole
 * truth: an image is "Ready" without ever having been parsed, and "Not processed" is a
 * state a person can act on rather than a bug they should report.
 */
function resolveStatusHint(status: DocumentStatus): string | null {
  if (status.kind === "ready") {
    if (status.readySource === "no_parsing_needed") return "No parsing needed";
    // A newer attempt that failed is *not* here: the status cell says it in its own words and
    // the popover carries the repair. Hover text is the one place a fact cannot be disclosed —
    // it does not exist on a touch screen and nobody reaches for it on a row that looks settled.
    //
    // Ready and readable — but this deployment learned to extract more from it since, and
    // only a reprocess will pick that up.
    return status.processingOutdated ? "New processing is available for this file" : null;
  }
  if (status.kind === "not_processed") {
    return "Nothing has read this file yet";
  }
  // Without this the word "Stopped" reads as something that happened *to* the file. It says
  // who stopped it and what the way back is, because the cell offers neither: a stop leaves
  // nothing to disclose, so there is no popover to carry the sentence.
  if (status.kind === "cancelled") {
    return "You stopped processing this file. Try again to run it.";
  }
  if (status.kind === "failed" && status.failure) {
    // Both sentences: the Status column is narrow enough that the inline cause is often
    // clipped, so the hover has to carry it in full, not only the next step.
    return `${status.failure.message} ${status.failure.nextStep}`;
  }
  return null;
}
